// LayerThumb — レイヤー行の「番号バッジ兼 選択ボタン」。サムネが届いたら、その層の縮小画像
// (市松の上) に番号を不透明な小札で重ねる。サムネが無い間 (設定 OFF / 自動停止 / まだ
// 届いていない層 / hub なし) は従来どおり番号だけ。Classic / Modern / スマホのシート共通。
//
// 画素は React を通さない: canvas は ref で hub に渡し、hub が `putImageData` で直接貼る
// (描き味の約束、layerThumbnailHub.ts)。サムネの出し入れは枠の `data-has-thumb` 属性と
// CSS (styles.css `.layer-thumb`) だけで切り替わるので、再レンダーは起きない。
//
// 選択ボタンを兼ねる: 行全体のクリック選択・キーボード・aria はこれまでの番号バッジと同じ。

import { useCallback, useEffect, useRef } from 'react';

import type { LayerThumbnailHub } from './layerThumbnailHub.js';

/** 長押しとみなす時間 (ms)。 */
export const LAYER_SOLO_HOLD_MS = 400;
/** これだけ動いたら長押しを取り消す (px。シートの縦スクロールと競合させない)。 */
export const LAYER_SOLO_MOVE_PX = 8;
/** 長押し後の互換 click を握りつぶす猶予 (ms)。click が来なかったときの後始末。 */
const SUPPRESS_CLICK_MS = 700;

export interface LayerThumbProps {
  readonly layerId: number;
  readonly hub: LayerThumbnailHub | null;
  readonly active: boolean;
  /** サムネが無いときの見た目 (大きさ・色)。サムネ表示中は枠の大きさを hub が上書きする。 */
  readonly className: string;
  readonly onSelect: () => void;
  readonly ariaLabel: string;
  readonly testId?: string;
  readonly dataManual?: string;
  /** 番号 (`layerId + 1`)。 */
  readonly children: React.ReactNode;
}

export const LayerThumb = ({
  layerId,
  hub,
  active,
  className,
  onSelect,
  ariaLabel,
  testId,
  dataManual,
  children,
}: LayerThumbProps): React.ReactNode => {
  const frameRef = useRef<HTMLButtonElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const frame = frameRef.current;
    const canvas = canvasRef.current;
    if (hub === null || frame === null || canvas === null) return;
    return hub.register(layerId, canvas, frame);
  }, [hub, layerId]);

  // 長押しで強調 (layer-thumbnails-2026-10-13.md)。状態は ref だけ: 再レンダーしない。
  const press = useRef<{
    pointerId: number;
    x: number;
    y: number;
    timer: ReturnType<typeof setTimeout> | null;
    soloing: boolean;
  } | null>(null);
  const suppressClick = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPress = useCallback(() => {
    const p = press.current;
    if (p === null) return;
    press.current = null;
    if (p.timer !== null) clearTimeout(p.timer);
    if (p.soloing) hub?.endSolo();
  }, [hub]);

  // パネルが閉じる / レイアウトが変わる最中に押されたままでも、強調を残さない。
  useEffect(
    () => () => {
      cancelPress();
      if (suppressClick.current !== null) clearTimeout(suppressClick.current);
    },
    [cancelPress],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (hub === null) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    cancelPress();
    const state = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      timer: null as ReturnType<typeof setTimeout> | null,
      soloing: false,
    };
    state.timer = setTimeout(() => {
      state.timer = null;
      if (press.current !== state) return;
      state.soloing = hub.beginSolo(layerId);
    }, LAYER_SOLO_HOLD_MS);
    press.current = state;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // capture できない環境 (jsdom 等) は無視
    }
  };
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const p = press.current;
    if (p === null || p.pointerId !== e.pointerId) return;
    if (Math.hypot(e.clientX - p.x, e.clientY - p.y) >= LAYER_SOLO_MOVE_PX) cancelPress();
  };
  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const p = press.current;
    if (p === null || p.pointerId !== e.pointerId) return;
    const wasSolo = p.soloing;
    cancelPress();
    if (wasSolo) {
      // 長押しは見るだけ。直後の互換 click (タッチ含む) で選択が変わらないようにする。
      if (suppressClick.current !== null) clearTimeout(suppressClick.current);
      suppressClick.current = setTimeout(() => {
        suppressClick.current = null;
      }, SUPPRESS_CLICK_MS);
    }
  };
  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (suppressClick.current !== null) {
      clearTimeout(suppressClick.current);
      suppressClick.current = null;
      e.stopPropagation(); // 行全体の選択クリックにも渡さない
      return;
    }
    onSelect();
  };

  return (
    <button
      ref={frameRef}
      type="button"
      aria-label={ariaLabel}
      aria-pressed={active}
      data-manual={dataManual}
      data-testid={testId}
      data-has-thumb="false"
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={cancelPress}
      onLostPointerCapture={cancelPress}
      onContextMenu={(e) => e.preventDefault()}
      className={`layer-thumb ${className}`}
    >
      <canvas ref={canvasRef} className="layer-thumb-canvas" />
      <span className="layer-thumb-num">{children}</span>
    </button>
  );
};
